/**
 * hybrid_player_adapter.ts
 *
 * IPlayerAdapter implementation for the JTT1078 relay WebSocket stream.
 *
 * Wire protocol (matches relay nalu_framer.py):
 *   MSG_VIDEO_INIT  (0x01): codec config + SPS/PPS — sent once, re-sent to late joiners
 *   MSG_VIDEO_FRAME (0x02): one reassembled frame (NALU Annex B) + pts
 *
 * Decoder selection (per-device, no UA sniffing):
 *   VideoDecoder.isConfigSupported() → 'webcodecs' (hardware GPU)
 *                                    → 'wasm'      (h265web.js Wasm, H.265 fallback)
 *
 * Test order:
 *   1. H.264 + WebCodecsDecoder  — always this path on modern browsers
 *   2. H.265 + WebCodecsDecoder  — Chrome/Edge/Safari Apple Silicon
 *   3. H.265 + WasmDecoder       — Firefox, Safari Intel, old Android
 */

import type { IPlayerAdapter, PlayerAdapterStats, PlayerFrameQualityChange } from 'livekit_web';
import { detectDecoderType, type DecoderType } from './detector';
import { WebCodecsDecoder } from './webcodecs_decoder';
import { WasmDecoder } from './wasm_decoder';
import { VisualArtifactProbe, isH265DebugEnabled } from './h265_debug';
import { FrameQualityGate } from './frame_quality_gate';
import { JitterBuffer } from './jitter_buffer';

// ── Wire format constants (must match relay nalu_framer.py) ─────────────────

const MSG_VIDEO_INIT = 0x01;
const MSG_VIDEO_FRAME = 0x02;

const CODEC_H264 = 0;
const CODEC_H265 = 1;

// ── VIDEO_INIT message parser ────────────────────────────────────────────────

interface VideoInitMsg {
  codec: number;
  width: number;
  height: number;
  fps: number;
  sps: Uint8Array;
  pps: Uint8Array;
}

function parseVideoInit(buf: ArrayBuffer): VideoInitMsg {
  const v = new DataView(buf);
  // Byte 0: type (0x01)
  // Byte 1: codec
  // Bytes 2-3: width (uint16 BE)
  // Bytes 4-5: height (uint16 BE)
  // Byte 6: fps
  let offset = 7;
  const spsLen = v.getUint16(offset, false); offset += 2;
  const sps = new Uint8Array(buf, offset, spsLen); offset += spsLen;
  const ppsLen = v.getUint16(offset, false); offset += 2;
  const pps = new Uint8Array(buf, offset, ppsLen);
  return {
    codec: v.getUint8(1),
    width: v.getUint16(2, false),
    height: v.getUint16(4, false),
    fps: v.getUint8(6),
    sps,
    pps,
  };
}

/**
 * Return every H.265 nal_unit_type present in an Annex B buffer (debug probe).
 * Used to verify that IDR ACUs carry VPS(32)+SPS(33)+PPS(34) before the slice.
 */
function _listAnnexBNalTypes(data: Uint8Array): number[] {
  const types: number[] = [];
  const n = data.length;
  let i = 0;
  while (i < n - 3) {
    let sc = -1, scLen = 0;
    for (let j = i; j < n - 2; j++) {
      if (data[j] === 0 && data[j + 1] === 0) {
        if (data[j + 2] === 1) { sc = j; scLen = 3; break; }
        if (j + 3 < n && data[j + 2] === 0 && data[j + 3] === 1) { sc = j; scLen = 4; break; }
      }
    }
    if (sc === -1) break;
    const off = sc + scLen;
    if (off < n) types.push((data[off] >> 1) & 0x3f);
    i = off + 1;
  }
  return types;
}

/**
 * Build the correct AVC codec string from a raw SPS NAL unit (with Annex B start code).
 * The SPS header contains profile_idc / constraint_set_flags / level_idc which together
 * form the codec string "avc1.PPCCLL" required by VideoDecoder.isConfigSupported().
 * Using the exact profile from the encoder avoids "Decoding error" when the encoder
 * outputs High Profile but the decoder is configured for Baseline.
 */
function buildH264CodecString(sps: Uint8Array): string {
  // Skip Annex B start code: [00 00 00 01] (4 bytes) or [00 00 01] (3 bytes)
  let offset = 0;
  if (sps.length >= 4 && sps[0] === 0 && sps[1] === 0 && sps[2] === 0 && sps[3] === 1) {
    offset = 4;
  } else if (sps.length >= 3 && sps[0] === 0 && sps[1] === 0 && sps[2] === 1) {
    offset = 3;
  }
  // offset → NAL type byte (0x67), offset+1 → profile_idc, offset+2 → constraints, offset+3 → level_idc
  if (sps.length < offset + 4) return 'avc1.42E01E'; // fallback: Baseline L3.0
  const profile = sps[offset + 1];
  const constraint = sps[offset + 2];
  const level = sps[offset + 3];
  return `avc1.${profile.toString(16).padStart(2, '0')}${constraint.toString(16).padStart(2, '0')}${level.toString(16).padStart(2, '0')}`;
}

type H265SampleEntry = 'hev1' | 'hvc1';

interface H265NalUnit {
  type: number;
  data: Uint8Array;
}

interface H265SpsConfig {
  generalProfileSpace: number;
  generalTierFlag: number;
  generalProfileIdc: number;
  generalProfileCompatibilityFlags: Uint8Array;
  generalConstraintIndicatorFlags: Uint8Array;
  generalLevelIdc: number;
  chromaFormatIdc: number;
  bitDepthLumaMinus8: number;
  bitDepthChromaMinus8: number;
  numTemporalLayers: number;
  temporalIdNested: boolean;
  picWidthInLumaSamples: number;
  picHeightInLumaSamples: number;
  /** Conformance-cropped display dimensions (the real picture size). */
  displayWidth: number;
  displayHeight: number;
}

interface H265DecoderConfigData {
  codecString: string;
  description?: Uint8Array;
  frameArea: number;
  /** Display dimensions parsed from the SPS (relay sends 0 in VIDEO_INIT). */
  width?: number;
  height?: number;
}

function _findH265NalUnit(sources: Uint8Array[], type: number): Uint8Array | undefined {
  for (const source of sources) {
    const unit = _splitAnnexBNalUnits(source).find((entry) => entry.type === type)?.data;
    if (unit) return unit;
  }
  return undefined;
}

function _equalUint8Arrays(left: Uint8Array | null, right: Uint8Array): boolean {
  if (!left || left.length !== right.length) return false;
  for (let i = 0; i < left.length; i++) {
    if (left[i] !== right[i]) return false;
  }
  return true;
}

function _splitAnnexBNalUnits(data: Uint8Array): H265NalUnit[] {
  const units: H265NalUnit[] = [];
  const n = data.length;
  let i = 0;
  let foundStartCode = false;

  while (i < n - 2) {
    let sc = -1;
    let scLen = 0;
    for (let j = i; j < n - 2; j++) {
      if (data[j] === 0 && data[j + 1] === 0) {
        if (data[j + 2] === 1) { sc = j; scLen = 3; break; }
        if (j + 3 < n && data[j + 2] === 0 && data[j + 3] === 1) { sc = j; scLen = 4; break; }
      }
    }
    if (sc === -1) break;

    foundStartCode = true;
    const nalStart = sc + scLen;
    if (nalStart >= n) break;

    let nalEnd = n;
    for (let j = nalStart + 1; j < n - 2; j++) {
      if (data[j] === 0 && data[j + 1] === 0 &&
          (data[j + 2] === 1 || (j + 3 < n && data[j + 2] === 0 && data[j + 3] === 1))) {
        nalEnd = j;
        break;
      }
    }

    units.push({
      type: (data[nalStart] >> 1) & 0x3f,
      data: data.subarray(nalStart, nalEnd),
    });
    i = nalEnd;
  }

  if (!foundStartCode && data.length > 0) {
    units.push({
      type: (data[0] >> 1) & 0x3f,
      data,
    });
  }

  return units;
}

function _removeEmulationPreventionBytes(data: Uint8Array): Uint8Array {
  const rbsp = new Uint8Array(data.length);
  let offset = 0;
  for (let i = 0; i < data.length; i++) {
    if (i >= 2 && data[i] === 0x03 && data[i - 1] === 0 && data[i - 2] === 0) {
      continue;
    }
    rbsp[offset++] = data[i];
  }
  return rbsp.subarray(0, offset);
}

class _BitReader {
  private bitPos = 0;

  constructor(private readonly data: Uint8Array) {}

  readBits(count: number): number {
    let value = 0;
    for (let i = 0; i < count; i++) {
      if (this.bitPos >= this.data.length * 8) throw new Error('Unexpected end of RBSP');
      const byte = this.data[this.bitPos >> 3];
      const bit = (byte >> (7 - (this.bitPos & 7))) & 1;
      value = (value << 1) | bit;
      this.bitPos++;
    }
    return value;
  }

  readBool(): boolean {
    return this.readBits(1) === 1;
  }

  skipBits(count: number): void {
    this.bitPos += count;
    if (this.bitPos > this.data.length * 8) throw new Error('Unexpected end of RBSP');
  }

  readUEV(): number {
    let leadingZeros = 0;
    while (this.readBits(1) === 0) leadingZeros++;
    if (leadingZeros === 0) return 0;
    return ((1 << leadingZeros) - 1) + this.readBits(leadingZeros);
  }
}

function _parseH265SpsConfig(spsNal: Uint8Array): H265SpsConfig | null {
  if (spsNal.length < 3) return null;

  try {
    const rbsp = _removeEmulationPreventionBytes(spsNal.subarray(2));
    const reader = new _BitReader(rbsp);

    reader.readBits(4); // sps_video_parameter_set_id
    const maxSubLayersMinus1 = reader.readBits(3);
    const temporalIdNested = reader.readBool();

    const generalProfileSpace = reader.readBits(2);
    const generalTierFlag = reader.readBits(1);
    const generalProfileIdc = reader.readBits(5);

    const generalProfileCompatibilityFlags = new Uint8Array(4);
    for (let i = 0; i < 4; i++) generalProfileCompatibilityFlags[i] = reader.readBits(8);

    const generalConstraintIndicatorFlags = new Uint8Array(6);
    for (let i = 0; i < 6; i++) generalConstraintIndicatorFlags[i] = reader.readBits(8);

    const generalLevelIdc = reader.readBits(8);

    const subLayerProfilePresentFlags: boolean[] = [];
    const subLayerLevelPresentFlags: boolean[] = [];
    for (let i = 0; i < maxSubLayersMinus1; i++) {
      subLayerProfilePresentFlags.push(reader.readBool());
      subLayerLevelPresentFlags.push(reader.readBool());
    }
    if (maxSubLayersMinus1 > 0) {
      for (let i = maxSubLayersMinus1; i < 8; i++) reader.skipBits(2);
    }
    for (let i = 0; i < maxSubLayersMinus1; i++) {
      if (subLayerProfilePresentFlags[i]) reader.skipBits(88);
      if (subLayerLevelPresentFlags[i]) reader.skipBits(8);
    }

    reader.readUEV(); // sps_seq_parameter_set_id
    const chromaFormatIdc = reader.readUEV();
    if (chromaFormatIdc === 3) reader.skipBits(1); // separate_colour_plane_flag
    const picWidthInLumaSamples = reader.readUEV();
    const picHeightInLumaSamples = reader.readUEV();
    // Conformance window crop — the encoder pads coded dimensions up to a CTB
    // multiple (e.g. 1080 → coded 1088) and crops back for display. Apply it so
    // the rendered aspect ratio matches the real picture.
    let confWinLeft = 0;
    let confWinRight = 0;
    let confWinTop = 0;
    let confWinBottom = 0;
    if (reader.readBool()) {
      confWinLeft = reader.readUEV();
      confWinRight = reader.readUEV();
      confWinTop = reader.readUEV();
      confWinBottom = reader.readUEV();
    }
    const subWidthC = chromaFormatIdc === 1 || chromaFormatIdc === 2 ? 2 : 1;
    const subHeightC = chromaFormatIdc === 1 ? 2 : 1;
    const displayWidth = picWidthInLumaSamples - subWidthC * (confWinLeft + confWinRight);
    const displayHeight = picHeightInLumaSamples - subHeightC * (confWinTop + confWinBottom);
    const bitDepthLumaMinus8 = reader.readUEV();
    const bitDepthChromaMinus8 = reader.readUEV();

    return {
      generalProfileSpace,
      generalTierFlag,
      generalProfileIdc,
      generalProfileCompatibilityFlags,
      generalConstraintIndicatorFlags,
      generalLevelIdc,
      chromaFormatIdc,
      bitDepthLumaMinus8,
      bitDepthChromaMinus8,
      numTemporalLayers: maxSubLayersMinus1 + 1,
      temporalIdNested,
      picWidthInLumaSamples,
      picHeightInLumaSamples,
      displayWidth,
      displayHeight,
    };
  } catch {
    return null;
  }
}

function _formatH265ConstraintPart(bytes: Uint8Array): string {
  let lastNonZero = -1;
  for (let i = bytes.length - 1; i >= 0; i--) {
    if (bytes[i] !== 0) {
      lastNonZero = i;
      break;
    }
  }
  if (lastNonZero < 0) return 'B0';
  return Array.from(bytes.subarray(0, lastNonZero + 1))
    .map((value) => value.toString(16).toUpperCase().padStart(2, '0'))
    .join('.');
}

function _buildH265CodecStringFromConfig(config: H265SpsConfig, sampleEntry: H265SampleEntry): string {
  const profileSpace = config.generalProfileSpace > 0
    ? String.fromCharCode(0x40 + config.generalProfileSpace)
    : '';
  const compatibility =
    ((config.generalProfileCompatibilityFlags[0] << 24) |
      (config.generalProfileCompatibilityFlags[1] << 16) |
      (config.generalProfileCompatibilityFlags[2] << 8) |
      config.generalProfileCompatibilityFlags[3]) >>> 0;
  const tier = config.generalTierFlag ? 'H' : 'L';
  const constraints = _formatH265ConstraintPart(config.generalConstraintIndicatorFlags);

  return `${sampleEntry}.${profileSpace}${config.generalProfileIdc}.${compatibility.toString(16).toUpperCase() || '0'}.${tier}${config.generalLevelIdc}.${constraints}`;
}

/**
 * Build an HEVC codec string from a raw SPS NAL unit (Annex B, H.265 SPS_NUT=33).
 *
 * The relay packs VPS+SPS together in the `sps` field of VIDEO_INIT, so this
 * scans the buffer to find SPS_NUT (type 33) and derives the sample entry.
 */
function buildH265CodecString(spsData: Uint8Array, sampleEntry: H265SampleEntry = 'hev1'): string {
  const sps = _splitAnnexBNalUnits(spsData).find((unit) => unit.type === 33)?.data;
  const config = sps ? _parseH265SpsConfig(sps) : null;
  return config ? _buildH265CodecStringFromConfig(config, sampleEntry) : `${sampleEntry}.1.6.L93.B0`;
}

function _buildHevcDecoderConfigurationRecord(
  vps: Uint8Array,
  sps: Uint8Array,
  pps: Uint8Array,
  config: H265SpsConfig,
): Uint8Array {
  const totalSize = 23 + (5 + vps.length) + (5 + sps.length) + (5 + pps.length);
  const data = new Uint8Array(totalSize);
  data[0] = 1;
  data[1] =
    ((config.generalProfileSpace & 0x03) << 6) |
    ((config.generalTierFlag & 0x01) << 5) |
    (config.generalProfileIdc & 0x1f);
  data.set(config.generalProfileCompatibilityFlags, 2);
  data.set(config.generalConstraintIndicatorFlags, 6);
  data[12] = config.generalLevelIdc;
  data[13] = 0xf0;
  data[14] = 0;
  data[15] = 0xfc;
  data[16] = 0xfc | (config.chromaFormatIdc & 0x03);
  data[17] = 0xf8 | (config.bitDepthLumaMinus8 & 0x07);
  data[18] = 0xf8 | (config.bitDepthChromaMinus8 & 0x07);
  data[19] = 0;
  data[20] = 0;
  data[21] =
    ((0 & 0x03) << 6) |
    ((config.numTemporalLayers & 0x07) << 3) |
    ((config.temporalIdNested ? 1 : 0) << 2) |
    0x03;
  data[22] = 0x03;

  let offset = 23;
  for (const unit of [
    { type: 32, data: vps },
    { type: 33, data: sps },
    { type: 34, data: pps },
  ]) {
    data[offset] = 0x80 | unit.type;
    data[offset + 1] = 0;
    data[offset + 2] = 1;
    data[offset + 3] = (unit.data.length >> 8) & 0xff;
    data[offset + 4] = unit.data.length & 0xff;
    data.set(unit.data, offset + 5);
    offset += 5 + unit.data.length;
  }

  return data;
}

function buildH265DecoderConfig(spsData: Uint8Array, ppsData: Uint8Array): H265DecoderConfigData {
  return buildH265DecoderConfigFromSources(spsData, ppsData);
}

function buildH265DecoderConfigFromSources(...sources: Uint8Array[]): H265DecoderConfigData {
  const usableSources = sources.filter((source) => source.length > 0);
  const primarySource = usableSources[0] ?? new Uint8Array(0);
  const vps = _findH265NalUnit(usableSources, 32);
  const sps = _findH265NalUnit(usableSources, 33);
  const pps = _findH265NalUnit(usableSources, 34);

  if (!sps) {
    return { codecString: buildH265CodecString(primarySource, 'hev1'), frameArea: 0 };
  }

  const config = _parseH265SpsConfig(sps);
  if (!config) {
    return { codecString: buildH265CodecString(primarySource, 'hev1'), frameArea: 0 };
  }

  const codecString = _buildH265CodecStringFromConfig(config, vps && pps ? 'hvc1' : 'hev1');
  if (!vps || !pps) {
    return {
      codecString,
      frameArea: config.picWidthInLumaSamples * config.picHeightInLumaSamples,
      width: config.displayWidth,
      height: config.displayHeight,
    };
  }

  return {
    codecString,
    description: _buildHevcDecoderConfigurationRecord(vps, sps, pps, config),
    frameArea: config.picWidthInLumaSamples * config.picHeightInLumaSamples,
    width: config.displayWidth,
    height: config.displayHeight,
  };
}

// ── Adapter ──────────────────────────────────────────────────────────────────

/** Runtime-tunable knobs for the JT/T1078 player path (wire from app env). */
export interface HybridPlayerAdapterOptions {
  /** Initial jitter-buffer depth (ms). Default 50. */
  jitterBufferInitialMs?: number;
  /** Max jitter-buffer depth the adaptive target may grow to (ms). Default 200. */
  jitterBufferMaxMs?: number;
  /**
   * Ms orphaned (no re-anchor keyframe) before the gate stops dropping and
   * admits P-frames onto the stale reference. 0 = never freeze. Default 800.
   */
  recoveringFallbackMs?: number;
}

export class HybridPlayerAdapter implements IPlayerAdapter {
  private ws: WebSocket | null = null;
  private detached = false;
  private canvas: HTMLCanvasElement | null = null;
  /** Container div for Wasm path — h265web.js appends its own canvas inside this. */
  private wasmContainer: HTMLDivElement | null = null;
  /** setInterval handle for the Wasm → 2D canvas relay (Firefox fallback). */
  private wasmRelayInterval: ReturnType<typeof setInterval> | null = null;
  private wcDecoder: WebCodecsDecoder | null = null;
  private wasmDecoder: WasmDecoder | null = null;
  private decoderType: DecoderType | null = null;
  private bytesReceived = 0;
  private startTime = 0;
  /** Frames buffered while async _handleVideoInit() is in progress. */
  private initQueue: ArrayBuffer[] = [];
  private initInProgress = false;
  private visualProbe: VisualArtifactProbe | null = null;
  /** H2 probe: log NAL structure of the first N IDR ACUs. */
  private _idrAcuLogCount = 0;
  private activeCodec: 'h264' | 'h265' | null = null;
  private activeCodecString: string | null = null;
  private activeDescription: Uint8Array | null = null;
  private activeWidth = 0;
  private activeHeight = 0;
  private activeH265FrameArea = 0;
  private initH265Sps = new Uint8Array(0);
  private initH265Pps = new Uint8Array(0);

  /**
   * @param wasmBaseUrl  Base URL for h265web.js Wasm static files.
   *                     Default '/static/' — must be served by the app.
   * @param opts         Tunables (typically wired from app env at build time):
   *                     - jitterBufferInitialMs / jitterBufferMaxMs: smoothing
   *                       buffer depth (deeper absorbs more burst/late arrival
   *                       at the cost of latency). Default 50 / 200.
   *                     - recoveringFallbackMs: after this long orphaned with no
   *                       re-anchor keyframe, admit P-frames onto the stale
   *                       reference (motion-but-artifact) instead of freezing.
   *                       0 = never freeze (always keep moving). Default 800.
   */
  constructor(
    private readonly wasmBaseUrl = '/static/',
    opts: HybridPlayerAdapterOptions = {},
  ) {
    this.gate = new FrameQualityGate({ recoveringFallbackMs: opts.recoveringFallbackMs });
    this.jitter = new JitterBuffer<Uint8Array>({
      initialTargetMs: opts.jitterBufferInitialMs,
      maxTargetMs: opts.jitterBufferMaxMs,
    });
  }

  // ── Frame quality gate + jitter buffer (JT/T1078 path) ───────────────────
  private readonly gate: FrameQualityGate;
  private readonly jitter: JitterBuffer<Uint8Array>;
  private gateUnsubscribe: (() => void) | null = null;
  private drainTimer: ReturnType<typeof setInterval> | null = null;
  private adaptTimer: ReturnType<typeof setInterval> | null = null;

  attach(
    uri: string,
    onReady: (stream: MediaStream) => void,
    onError: (err: Error) => void,
    onFrameQuality?: (change: PlayerFrameQualityChange) => void,
  ): void {
    this.detached = false;
    this.ws = new WebSocket(uri);
    this.ws.binaryType = 'arraybuffer';
    this.startTime = performance.now();
    this.bytesReceived = 0;

    if (onFrameQuality) {
      this.gateUnsubscribe?.();
      this.gateUnsubscribe = this.gate.onChange((change) => {
        try { onFrameQuality(change); } catch (err) {
          console.error('[HybridPlayerAdapter] frame quality listener threw:', err);
        }
      });
    }
    // 60 Hz drain so steady cadence keeps going during network silence.
    this.drainTimer = setInterval(() => this._drainAndDecode(), 16);
    // 1 Hz adapt of jitter target.
    this.adaptTimer = setInterval(() => this.jitter.adapt(), 1000);

    let _dbgFrameCount = 0;
    this.ws.onopen = () => console.info('[HybridPlayerAdapter] WS open', uri);
    this.ws.onmessage = (ev: MessageEvent<ArrayBuffer>) => {
      const buf = ev.data;
      const msgType = new Uint8Array(buf)[0];
      this.bytesReceived += buf.byteLength;

      if (msgType === MSG_VIDEO_INIT) {
        console.info('[HybridPlayerAdapter] MSG_VIDEO_INIT received, initInProgress=', this.initInProgress, 'decoderType=', this.decoderType);
        // Only process the first VIDEO_INIT (or if decoder not yet configured).
        // Re-init mid-stream is not yet supported.
        if (!this.initInProgress && !this.decoderType) {
          this.initInProgress = true;
          this._handleVideoInit(buf, onReady, onError).catch((err) => {
            console.error('[HybridPlayerAdapter] init failed:', err);
            onError(err instanceof Error ? err : new Error(String(err)));
          });
        }
      } else if (msgType === MSG_VIDEO_FRAME) {
        _dbgFrameCount++;
        if (isH265DebugEnabled() && (_dbgFrameCount <= 5 || _dbgFrameCount % 100 === 0)) {
          console.info('[HybridPlayerAdapter] MSG_VIDEO_FRAME #' + _dbgFrameCount + ' initInProgress=' + this.initInProgress + ' queueLen=' + this.initQueue.length);
        }
        if (this.initInProgress) {
          // Buffer frames received during async init — flushed after configure().
          this.initQueue.push(buf);
        } else {
          this._handleVideoFrame(buf);
        }
      }
    };

    this.ws.onerror = (e) => {
      console.error('[HybridPlayerAdapter] WS error', e);
      onError(new Error('[HybridPlayerAdapter] WebSocket error'));
    };
    this.ws.onclose = (e) => {
      console.warn('[HybridPlayerAdapter] WS closed code=', e.code, 'reason=', e.reason, 'wasClean=', e.wasClean);
      if (!this.detached) {
        onError(new Error(`[HybridPlayerAdapter] WebSocket closed unexpectedly (code=${e.code})`));
      }
    };
  }

  private async _handleVideoInit(
    buf: ArrayBuffer,
    onReady: (stream: MediaStream) => void,
    onError: (err: Error) => void,
  ): Promise<void> {
    const init = parseVideoInit(buf);
    const codec = init.codec === CODEC_H265 ? 'h265' : 'h264';
    const h265Config = codec === 'h265' ? buildH265DecoderConfig(init.sps, init.pps) : null;
    const codecString =
      codec === 'h264' ? buildH264CodecString(init.sps) : (h265Config?.codecString ?? buildH265CodecString(init.sps));
    const description = codec === 'h265' && h265Config?.description
      ? new Uint8Array(h265Config.description)
      : undefined;
    this.activeCodec = codec;
    this.activeCodecString = codecString;
    this.activeDescription = description ?? null;
    // Relay sends 0×0 in VIDEO_INIT; fall back to the dimensions parsed from
    // the H265 SPS conformance window so the canvas/decoder get the real
    // resolution (and aspect ratio) from the very first frame.
    const resolvedWidth = init.width || h265Config?.width || 0;
    const resolvedHeight = init.height || h265Config?.height || 0;
    this.activeWidth = resolvedWidth;
    this.activeHeight = resolvedHeight;
    this.activeH265FrameArea = h265Config?.frameArea ?? 0;
    this.initH265Sps = codec === 'h265' ? new Uint8Array(init.sps) : new Uint8Array(0);
    this.initH265Pps = codec === 'h265' ? new Uint8Array(init.pps) : new Uint8Array(0);
    console.info(`[HybridPlayerAdapter] codec string: ${codecString}${description ? ' + hvcc' : ''}`);
    this.decoderType = await detectDecoderType(codec, codecString, description);

    // Fail-fast: WASM renderer only supports 8-bit YUV420P. If the H265 stream
    // uses 10-bit (bitDepthLumaMinus8 > 0) and WebCodecs is not available (WASM
    // path was selected), surface a clear error instead of showing a black canvas.
    if (this.decoderType === 'wasm' && codec === 'h265') {
      const spsNal = _findH265NalUnit([init.sps], 33);
      if (spsNal) {
        const spsConfig = _parseH265SpsConfig(spsNal);
        if (spsConfig && spsConfig.bitDepthLumaMinus8 > 0) {
          const bitDepth = spsConfig.bitDepthLumaMinus8 + 8;
          console.warn(`[HybridPlayerAdapter] ${bitDepth}-bit H265 detected on WASM path — not renderable`);
          const err = new Error(
            `Video HD dùng định dạng H.265 ${bitDepth}-bit không được hỗ trợ bởi trình giải mã phần mềm. ` +
            `Vui lòng chuyển về chất lượng SD hoặc dùng trình duyệt Chrome/Edge trên máy có phần cứng hỗ trợ H.265.`,
          );
          (err as Error & { nonRetryable?: boolean }).nonRetryable = true;
          this.initInProgress = false;
          onError(err);
          return;
        }
      }
    }

    // Resolved width/height — VIDEO_INIT is 0×0, prefer SPS-parsed dims; only
    // fall back to a 4:3 placeholder if neither is available (e.g. H264 path).
    const w = resolvedWidth || 640;
    const h = resolvedHeight || 480;
    const fps = init.fps || 25;

    // Reset frame-quality gate + jitter buffer for the new stream and seed
    // the gate's PTS-gap threshold from the encoder's fps.
    this.gate.reset();
    this.gate.setExpectedFrameIntervalMs(1000 / fps);
    this.jitter.reset();

    if (this.decoderType === 'webcodecs') {
      // WebCodecs path: create an off-screen canvas; decoder renders into it.
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      canvas.style.cssText =
        'position:fixed;top:-2px;left:-2px;width:1px;height:1px;opacity:0;pointer-events:none';
      document.body.appendChild(canvas);
      this.canvas = canvas;

      this.wcDecoder = new WebCodecsDecoder(canvas);
      // Pipe decoder errors into the gate so a rejected bitstream flips us
      // into 'recovering' before the next P-frame poisons the canvas.
      this.wcDecoder.onDecoderError(() => this.gate.notifyDecoderError());
      try {
        this.wcDecoder.configure(codec, resolvedWidth, resolvedHeight, codecString, description);
      } catch (err) {
        onError(err instanceof Error ? err : new Error(String(err)));
        return;
      }
      // Visual artifact probe for the WebCodecs render target. The canvas is
      // off-screen but drawImage() readback works regardless of compositor.
      if (isH265DebugEnabled()) {
        this.visualProbe = new VisualArtifactProbe(canvas);
        this.visualProbe.start();
      }
    } else {
      // Wasm path (H.265 fallback on Firefox / Safari Intel / old Android):
      // h265web.js expects a <div> container identified by an id. It creates a
      // new <canvas> element inside that div for its WebGL rendering context.
      // We must capture THAT canvas — not an outer placeholder — for MediaStream.
      const containerId = `vgps-wasm-${Date.now()}`;
      const container = document.createElement('div');
      container.id = containerId;
      // Hide off-screen; must remain in document so WebGL context stays alive.
      container.style.cssText =
        'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;overflow:hidden;pointer-events:none';
      document.body.appendChild(container);
      this.wasmContainer = container;

      this.wasmDecoder = new WasmDecoder(containerId);
      try {
        await this.wasmDecoder.build({
          wasmBaseUrl: this.wasmBaseUrl,
          width: w,
          height: h,
          fps,
        });
        // initLiveDecoder sends probe_ok to the decoder worker so it initialises
        // its WebGL context. The h265web.js patch defers this asynchronously
        // (up to ~1 s) to ensure the Wasm module has finished loading inside the
        // worker. We MUST await it so that the initQueue flush below does not
        // race — frames posted before probe_ok arrives in the worker would be
        // decoded without a codec context, producing POC errors and fast-forward.
        await this.wasmDecoder.initLiveDecoder(w, h);
      } catch (err) {
        onError(err instanceof Error ? err : new Error(String(err)));
        return;
      }

      // h265web.js creates a <canvas> synchronously inside the container during
      // build(). Use that canvas for captureStream() so we capture Wasm output.
      const wasmCanvas = container.querySelector('canvas') as HTMLCanvasElement | null;
      if (!wasmCanvas) {
        onError(new Error('[HybridPlayerAdapter] Wasm decoder did not create a canvas in the container'));
        return;
      }
      this.canvas = wasmCanvas;
    }

    // Start streaming canvas output as MediaStream via a 2D canvas intermediary.
    //
    // captureStream() on a WebGL canvas is unreliable: Chrome only composites
    // visible elements, so an off-screen canvas (-9999px) never produces frames
    // even with requestFrame(). The 2D canvas approach avoids the compositor
    // entirely: drawImage(webglCanvas) does a direct GPU→CPU readback (works even
    // off-screen), then requestFrame() on the 2D captureStream delivers the frame.
    //
    // The WebGL canvas has preserveDrawingBuffer:true (h265web.js patch) so the
    // framebuffer is still valid when drawImage() is called in the callback.
    let stream: MediaStream;
    if (this.wasmDecoder && this.canvas) {
      const webglCanvas = this.canvas;
      const canvas2d = document.createElement('canvas');
      canvas2d.width = webglCanvas.width || w;
      canvas2d.height = webglCanvas.height || h;
      // Keep it hidden but attached to the DOM inside the Wasm container so it is
      // cleaned up automatically when the container is removed on detach().
      canvas2d.style.cssText = 'position:absolute;top:0;left:0;visibility:hidden;pointer-events:none;';
      (this.wasmContainer ?? document.body).appendChild(canvas2d);

      const ctx2d = canvas2d.getContext('2d');

      // Generic relay tick: keep canvas2d sized to whatever h265web.js' WebGL
      // canvas currently is (it can change on SD↔HD quality switch), then copy
      // the framebuffer over. This single helper is used by every browser path
      // below — no per-browser drawing logic.
      //
      // Without the resize, drawImage(webglCanvas, 0, 0) copies pixel-for-pixel
      // and you'd only see the top-left region of the new resolution
      // (e.g. SD-sized 640×360 view of an HD 1280×720 frame).
      const relayDraw = (): void => {
        try {
          const sw = webglCanvas.width;
          const sh = webglCanvas.height;
          if (sw > 0 && sh > 0 && (canvas2d.width !== sw || canvas2d.height !== sh)) {
            canvas2d.width = sw;
            canvas2d.height = sh;
          }
          ctx2d!.drawImage(webglCanvas, 0, 0);
        } catch {
          // WebGL canvas may be gone during teardown; ignore.
        }
      };

      // Capability detection (no UA sniffing). Each capability gives us a
      // different driver:
      //   - canvas.captureStream + MediaStreamTrack.requestFrame  → callback-driven
      //     (Chromium, Safari ≥ 15, Edge, Opera, Coc Coc): cheapest, only paints
      //     when the decoder actually produced a frame.
      //   - canvas.captureStream(fps) only                        → interval-driven
      //     (Firefox, older Safari, anything missing requestFrame): paints at
      //     wall-clock fps regardless of decoder activity.
      //   - neither                                               → direct WebGL
      //     captureStream (last-resort, low quality off-screen).
      // Adding support for a new browser/version normally needs no code change;
      // it falls into one of the existing branches automatically.
      const videoTrackProbe = ctx2d
        ? (canvas2d.captureStream(0).getVideoTracks()[0] as
            (CanvasCaptureMediaStreamTrack & { requestFrame?: () => void }) | undefined)
        : undefined;
      const hasRequestFrame = typeof videoTrackProbe?.requestFrame === 'function';

      if (ctx2d && hasRequestFrame) {
        stream = canvas2d.captureStream(0);
        const videoTrack = stream.getVideoTracks()[0] as
          CanvasCaptureMediaStreamTrack & { requestFrame: () => void };
        const rf = videoTrack.requestFrame.bind(videoTrack);
        this.wasmDecoder.setVideoRenderCallback(() => {
          relayDraw();
          rf();
        });
        console.info('[HybridPlayerAdapter] 2D-canvas captureStream relay installed (callback-driven)');
      } else if (ctx2d) {
        // h265web.js' video_render_callback may not fire reliably here, so we
        // pace the copy ourselves. captureStream(fps) auto-emits at that rate;
        // preserveDrawingBuffer:true (h265web.js patch) keeps the WebGL
        // framebuffer valid between paints.
        const targetFps = Math.max(1, Math.min(60, Math.round(fps || 25)));
        stream = canvas2d.captureStream(targetFps);
        const intervalMs = Math.max(16, Math.round(1000 / targetFps));
        this.wasmRelayInterval = setInterval(relayDraw, intervalMs);
        console.info(
          `[HybridPlayerAdapter] 2D-canvas captureStream relay installed ` +
          `(interval-driven @${targetFps}fps, every ${intervalMs}ms)`,
        );
      } else {
        stream = webglCanvas.captureStream(Math.max(1, Math.round(fps || 25)));
        console.warn('[HybridPlayerAdapter] 2D ctx unavailable, using direct WebGL captureStream');
      }

      // Visual probe runs on the 2D canvas (already kept in sync with the
      // WebGL output). Gated by the H.265 debug flag so production stays silent.
      if (ctx2d && isH265DebugEnabled()) {
        this.visualProbe = new VisualArtifactProbe(canvas2d);
        this.visualProbe.start();
      }

      // Start the cylopengl render loop. Without this, the ffdecoder worker
      // decodes frames but they are never popped from the Wasm cache and drawn.
      this.wasmDecoder.startRender();
      console.info('[HybridPlayerAdapter] WASM render loop started');
    } else {
      stream = (this.canvas as HTMLCanvasElement).captureStream(fps);
    }

    console.info('[HybridPlayerAdapter] onReady stream tracks=', stream.getTracks().length, 'decoderType=', this.decoderType, 'queueLen=', this.initQueue.length);
    onReady(stream);

    // Discard frames buffered during async WASM init.
    //
    // The queue contains P-frames from BEFORE the first live IDR.  Feeding
    // these stale frames to the WASM decoder corrupts its DPB: even though
    // WasmDecoder.feedNalu drops pre-IDR P-frames, the P-frames AFTER the
    // queued IDR reference GOP positions that are then misaligned with the
    // ongoing live stream, triggering avcodec "Could not find ref with POC N"
    // errors → ffdecoder sets seek_pending=true → drops every frame until the
    // next IDR → ~1-second video freeze.
    //
    // By discarding the queue, the decoder starts clean with needsKeyframe=true
    // and shows video at the next live IDR (≤ gop_size/fps seconds away,
    // typically ≤ 1 second with gop_size=fps=15).
    const discardCount = this.initQueue.length;
    this.initQueue = [];
    this.initInProgress = false;
    console.info('[HybridPlayerAdapter] discarded', discardCount, 'stale queued frames — waiting for next live IDR');
  }

  private _maybeUpgradeH265ConfigFromKeyframe(nalu: Uint8Array): void {
    if (
      this.decoderType !== 'webcodecs' ||
      this.activeCodec !== 'h265' ||
      !this.wcDecoder
    ) {
      return;
    }

    const h265Config = buildH265DecoderConfigFromSources(nalu, this.initH265Sps, this.initH265Pps);
    if (!h265Config.description) return;

    if (this.activeH265FrameArea > 0 && h265Config.frameArea > 0 && h265Config.frameArea < this.activeH265FrameArea) {
      if (isH265DebugEnabled()) {
        console.info(
          `[HybridPlayerAdapter] ignoring smaller H.265 live config: ` +
          `${h265Config.codecString} area=${h265Config.frameArea} < active=${this.activeH265FrameArea}`,
        );
      }
      return;
    }

    const description = new Uint8Array(h265Config.description);
    if (
      this.activeCodecString === h265Config.codecString &&
      _equalUint8Arrays(this.activeDescription, description)
    ) {
      return;
    }

    try {
      console.info(
        `[HybridPlayerAdapter] reconfiguring H.265 from live keyframe: ` +
        `${this.activeCodecString ?? 'unset'} -> ${h265Config.codecString} + hvcc`,
      );
      this.wcDecoder.configure(
        'h265',
        this.activeWidth,
        this.activeHeight,
        h265Config.codecString,
        description,
      );
      this.activeCodecString = h265Config.codecString;
      this.activeDescription = description;
      this.activeH265FrameArea = h265Config.frameArea;
    } catch (err) {
      console.warn('[HybridPlayerAdapter] failed to upgrade H.265 config from live keyframe:', err);
    }
  }

  private _handleVideoFrame(buf: ArrayBuffer): void {
    if (!this.decoderType) return;

    const v = new DataView(buf);
    // Byte 0: type (0x02)
    // Byte 1: flags (bit0=keyframe, bit1=codec)
    // Bytes 2-9: pts_us (int64 BE)
    // Bytes 10-13: data_len (uint32 BE)
    // Bytes 14+: nalu data
    const flags = v.getUint8(1);
    const isKeyframe = (flags & 0x01) === 1;
    const ptsUs = Number(v.getBigInt64(2, false));
    const dataLen = v.getUint32(10, false);
    const nalu = new Uint8Array(buf, 14, dataLen);

    // H2 probe: log the Annex B NAL types present in the first 10 IDR ACUs.
    // Confirms whether VPS(32)+SPS(33)+PPS(34) are prepended before every IDR.
    if (isKeyframe && this._idrAcuLogCount < 10 && isH265DebugEnabled()) {
      this._idrAcuLogCount++;
      const types = _listAnnexBNalTypes(nalu);
      console.info(`[h265-event] IDR ACU #${this._idrAcuLogCount} nal_types=[${types.join(',')}]`);
    }

    if (isKeyframe) {
      this._maybeUpgradeH265ConfigFromKeyframe(nalu);
    }

    // Push into the jitter buffer; the gate-aware drain feeds the decoder.
    this.jitter.push(nalu, ptsUs, isKeyframe);
    this._drainAndDecode();
  }

  /**
   * Drain frames aged past the jitter buffer target, gate each one, feed
   * admitted frames into the active decoder. Called from the 60 Hz drainTimer
   * and from `_handleVideoFrame` for immediate-drain steady cadence.
   */
  private _drainAndDecode(): void {
    if (!this.decoderType) return;
    const drained = this.jitter.drain();
    for (const f of drained) {
      if (!this.gate.admit(f.ptsUs, f.isKeyframe)) continue;
      if (this.decoderType === 'webcodecs') {
        this.wcDecoder?.decode(f.frame, f.isKeyframe, f.ptsUs);
      } else {
        this.wasmDecoder?.feedNalu(f.frame, f.isKeyframe, f.ptsUs);
      }
    }
  }

  detach(): void {
    this.detached = true;
    this.ws?.close();
    this.ws = null;

    if (this.wasmRelayInterval !== null) {
      clearInterval(this.wasmRelayInterval);
      this.wasmRelayInterval = null;
    }
    if (this.drainTimer !== null) { clearInterval(this.drainTimer); this.drainTimer = null; }
    if (this.adaptTimer !== null) { clearInterval(this.adaptTimer); this.adaptTimer = null; }
    if (this.gateUnsubscribe) { this.gateUnsubscribe(); this.gateUnsubscribe = null; }
    this.gate.reset();
    this.jitter.reset();

    this.visualProbe?.stop();
    this.visualProbe = null;

    this.initQueue = [];
    this.initInProgress = false;
    this.decoderType = null;
    this.activeCodec = null;
    this.activeCodecString = null;
    this.activeDescription = null;
    this.activeWidth = 0;
    this.activeHeight = 0;
    this.activeH265FrameArea = 0;
    this.initH265Sps = new Uint8Array(0);
    this.initH265Pps = new Uint8Array(0);

    this.wcDecoder?.destroy();
    this.wcDecoder = null;

    this.wasmDecoder?.destroy();
    this.wasmDecoder = null;

    this.canvas?.remove();
    this.canvas = null;

    this.wasmContainer?.remove();
    this.wasmContainer = null;

    this.decoderType = null;
  }

  getStats(): PlayerAdapterStats {
    const elapsed = (performance.now() - this.startTime) / 1000;
    const videoBitrateKbps =
      elapsed > 0 ? (this.bytesReceived * 8) / elapsed / 1000 : 0;

    const s = this.wcDecoder?.getStats() ?? this.wasmDecoder?.getStats() ?? { decodedFrames: 0, droppedFrames: 0 };
    return {
      videoBitrateKbps,
      decodedFrames: s.decodedFrames,
      droppedFrames: s.droppedFrames,
      // Extra fields for Stats panel
      bytesReceived: this.bytesReceived,
      frameWidth: 'frameWidth' in s ? (s.frameWidth as number | undefined) : undefined,
      frameHeight: 'frameHeight' in s ? (s.frameHeight as number | undefined) : undefined,
      framesPerSecond: 'framesPerSecond' in s ? (s.framesPerSecond as number | undefined) : undefined,
      framesDropped: s.droppedFrames,
      decoderImplementation: this.decoderType === 'webcodecs' ? 'WebCodecs' : this.decoderType === 'wasm' ? 'Wasm' : undefined,
      streamCodec: this.activeCodec ?? undefined,
      decoderCodec: this.activeCodecString ?? undefined,
    } as PlayerAdapterStats;
  }
}
